import React from "react";
import { TextField, TextFieldProps, Typography, styled } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../store/Index";

const FieldLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "textcolor",
})<{ textcolor?: string }>(({ textcolor }) => ({
  fontSize: "13px",
  fontWeight: 500,
  opacity: 0.9,
  color: textcolor,
  marginBottom: "6px",
  lineHeight: 1.4,

  "& span.required": {
    color: "#ff4d4f",
    marginLeft: "2px",
  },
}));

const StyledInput = styled(TextField, {
  shouldForwardProp: (prop) =>
    prop !== "inputbg" && prop !== "textcolor" && prop !== "placeholdercolor",
})<{ inputbg?: string; textcolor?: string; placeholdercolor?: string }>(
  ({ inputbg, textcolor, placeholdercolor }) => ({
    width: "100%",
    "& .MuiOutlinedInput-root": {
      backgroundColor: inputbg,
      borderRadius: "30px",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 14px",
      fontSize: "13px",
      color: textcolor,
      "&::placeholder": {
        color: placeholdercolor,
        opacity: 0.9,
      },
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 0,
      marginTop: "2px",
      fontSize: "0.7rem",
      lineHeight: 1.2,
    },
  }),
);

export interface PillTextFieldProps extends Omit<TextFieldProps, "variant"> {
  label?: string;
  required?: boolean;
  inputbg?: string;
  textcolor?: string;
  placeholdercolor?: string;
  labelColor?: string;
}

const PillTextField: React.FC<PillTextFieldProps> = ({
  label,
  required,
  inputbg,
  textcolor,
  placeholdercolor,
  labelColor,
  ...props
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const color100 = reduxTheme.primary_colors?.[100];
  const color800 = reduxTheme.primary_colors?.[800];

  return (
    <>
      {label && (
        <FieldLabel textcolor={labelColor ?? color100}>
          {label}
          {required && <span className="required">*</span>}
        </FieldLabel>
      )}
      <StyledInput
        variant="outlined"
        inputbg={inputbg ?? color800}
        textcolor={textcolor}
        placeholdercolor={placeholdercolor}
        {...props}
      />
    </>
  );
};

export default PillTextField;
